import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

// Two-step KR-only auto-invite:
//   (1) GET /participants/audience-preview — diagnostic-only sample counts
//   (2) POST /participants { invite_all_eligible_audience: true } — backend
//       re-resolves the audience atomically. No user list is ever sent from
//       the client.
export default function InviteAllEligibleDialog({ open, event, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('preview'); // 'preview' | 'busy' | 'result'
  const [result, setResult] = useState(null);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    if (!event?.event_id) return;
    setLoading(true);
    try {
      const r = await calendarAPI.previewAudience(event.event_id);
      setPreview(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Preview failed'));
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [event, onClose]);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setResult(null);
    setStep('preview');
    setNotes('');
    load();
  }, [open, load]);

  const commit = async () => {
    setStep('busy');
    try {
      const r = await calendarAPI.inviteAllEligible(event.event_id, notes.trim() || null);
      setResult(r.data);
      setStep('result');
      onDone?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Invite failed'));
      setStep('preview');
    }
  };

  const disabled =
    !preview
    || preview.notice
    || (preview.eligible_total ?? 0) === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && step !== 'busy' && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="invite-all-eligible-dialog">
        <DialogHeader>
          <DialogTitle>Invite All Eligible Audience</DialogTitle>
          <DialogDescription>
            The server re-resolves the audience at execution time from
            <em> visibility scope</em> and <em>audience</em> fields — the client never sends a user list.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="invite-all-loading">
            Resolving audience…
          </div>
        )}

        {step === 'preview' && !loading && preview && (
          <div className="space-y-3" data-testid="invite-all-preview">
            {preview.notice && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="invite-all-notice">
                {preview.notice}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Cell label="Audience found" value={preview.audience_users_found} testid="preview-audience-found" />
              <Cell label="Already invited" value={preview.already_assigned} testid="preview-already-invited" />
              <Cell label="Eligible to invite" value={preview.eligible_total} testid="preview-eligible-total" />
              <Cell label="Inactive/invalid" value={preview.inactive_or_invalid} testid="preview-inactive" />
              <Cell label="Available capacity" value={preview.available_capacity == null ? 'no cap' : preview.available_capacity} testid="preview-capacity" />
              <Cell label="Would skip (capacity)" value={preview.skipped_over_capacity} testid="preview-skipped-capacity" />
            </div>
            {preview.sample_eligible_user_ids?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600">
                  Sample eligible user IDs (up to {preview.sample_limit})
                </summary>
                <ul className="mt-1 max-h-24 overflow-y-auto pl-4 list-disc text-slate-600 space-y-0.5">
                  {preview.sample_eligible_user_ids.map((id) => <li key={id}>{id}</li>)}
                </ul>
              </details>
            )}
            <div>
              <Label className="text-xs text-slate-600">Notes (KR-internal, optional)</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="invite-all-notes"
              />
            </div>
            <p className="text-[11px] text-slate-500">
              Final counts (created / skipped) will be returned after commit — the backend re-resolves the
              audience atomically to prevent stale-preview drift.
            </p>
          </div>
        )}

        {step === 'busy' && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="invite-all-busy">Inviting…</div>
        )}

        {step === 'result' && result && (
          <div className="space-y-3" data-testid="invite-all-result">
            <p className="text-sm text-slate-700">Audience resolved and invited.</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Cell label="Audience found" value={result.audience_found} testid="result-audience-found" />
              <Cell label="Already assigned" value={result.already_assigned} testid="result-already-assigned" />
              <Cell label="Created" value={result.created} tone="emerald" testid="result-created" />
              <Cell label="Inactive/invalid" value={result.inactive_or_invalid} testid="result-inactive" />
              <Cell label="Skipped (capacity)" value={result.skipped_capacity} tone={result.skipped_capacity > 0 ? 'amber' : 'slate'} testid="result-skipped-capacity" />
              <Cell label="Capacity reached" value={result.capacity_reached ? 'Yes' : 'No'} tone={result.capacity_reached ? 'amber' : 'slate'} testid="result-capacity-reached" />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={onClose} data-testid="invite-all-cancel">Cancel</Button>
              <Button
                onClick={commit}
                disabled={disabled}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="invite-all-confirm"
                title={disabled ? (preview?.notice || 'Nothing to invite') : ''}
              >
                Confirm & Invite {preview?.eligible_total || 0}
              </Button>
            </>
          )}
          {step === 'busy' && <Button disabled>Please wait…</Button>}
          {step === 'result' && (
            <Button onClick={onClose} data-testid="invite-all-close-result">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Cell = ({ label, value, testid, tone }) => {
  const toneCls = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
  }[tone] || 'border-slate-200 bg-slate-50 text-slate-800';
  return (
    <div className={`rounded border p-2 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
};
